/**
 * Service that monitors microphone health by checking audio levels.
 */
import livestreamActions from '../../actions/LivestreamActions';
import storeManager from '../../store/StoreManager';
import sessionReader from '../../store/readHelpers/SessionReader';
import {
    calculateAudioBarLevel,
} from './MicrophoneAudioLevelConverter';

// Constants for mic health check
const MIC_HEALTH_CHECK_TIMEOUT_MS = 10000; // 10 seconds timeout
// Match the old version's segmentation
const TOTAL_SEGMENTS = 10;
const LOW_ZONE_SEGMENTS = 2;
// Mic health check states
export const MIC_HEALTH_CHECK_STATUS = {
    UNDIAGNOSED: 'UNDIAGNOSED',
    PASSES: 'PASSES',
    FAILED: 'FAILED',
};

let audioContext = null;
let analyser = null;
let dataArray = null;
let source = null;
let checkInterval = null;
let timeoutId = null;

/**
 * Initialize the audio context and analyser for monitoring audio levels
 * @param {MediaStream} stream - The audio stream to monitor
 */
const initializeAudioAnalyser = (stream) => {
    // Create audio context if it doesn't exist, using a defensive global lookup
    if (!audioContext) {
        let globalObj = {};
        if (typeof window !== 'undefined') {
            globalObj = window;
        } else if (typeof self !== 'undefined') {
            globalObj = self;
        }
        const AudioCtxCtor = globalObj.AudioContext || globalObj.webkitAudioContext;
        if (typeof AudioCtxCtor === 'function') {
            audioContext = new AudioCtxCtor();
        } else {
            // AudioContext not supported or unavailable in this environment
            // Leave analyser null so checks gracefully no-op
            return;
        }
    }

    // Create analyser node
    analyser = audioContext.createAnalyser();
    analyser.fftSize = 256;

    // Create buffer for audio data
    const bufferLength = analyser.frequencyBinCount;
    dataArray = new Uint8Array(bufferLength);

    // Connect the audio source to the analyser
    source = audioContext.createMediaStreamSource(stream);
    source.connect(analyser);
};

/**
 * Get audio level using the same method as the old version
 * @returns {number} - Current segment level (0-10)
 */
const getAudioLevel = () => {
    if (!analyser || !dataArray) {
        return 0;
    }
    try {
        return calculateAudioBarLevel(analyser, TOTAL_SEGMENTS);
    } catch (error) {
        // Add error handling for WebAudio API calls
        console.error('Mic health check error:', {
            error,
            context: 'getByteTimeDomainData failed',
        });
        return 0;
    }
};

/**
 * Check if the audio level is above the threshold
 * @returns {boolean} - True if audio level is above threshold
 */
const isAudioLevelAboveThreshold = () => {
    const segmentLevel = getAudioLevel();
    // Match old version: if val > avPageMicVolumeNumLowSegs
    return segmentLevel > LOW_ZONE_SEGMENTS;
};

/**
 * Clean up resources used by the mic health checker
 */
const cleanup = () => {
    if (checkInterval) {
        clearInterval(checkInterval);
        checkInterval = null;
    }

    if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
    }

    if (source) {
        source.disconnect();
        source = null;
    }

    // Reset analyser and data buffer
    analyser = null;
    dataArray = null;

    // Ensure we don't hold onto a stale AudioContext across runs/tests
    // If running in a real browser and close() exists, try to close gracefully
    if (audioContext && typeof audioContext.close === 'function') {
        try {
            // Only close if not already closed
            if (audioContext.state === 'running' || audioContext.state === 'suspended') {
                // close() returns a Promise; ignore rejections from mocks/unsupported envs
                const closeResult = audioContext.close();
                if (closeResult && typeof closeResult.catch === 'function') {
                    closeResult.catch(() => {});
                }
            }
        } catch (e) {
            // ignore errors from mock or unsupported environments
            console.log('Unable to close audio context gracefully', e);
        }
    }
    audioContext = null;
};

const micHealthChecker = {
    /**
     * Start monitoring the microphone health
     * @param {MediaStream} stream - The audio stream to monitor
     */
    startMonitoring(stream) {
        // Only start monitoring if the feature is enabled
        if (!sessionReader.isRemoteProctoringMicHealthCheckEnabled()) {
            return;
        }

        // Check if stream has audio tracks
        if (!stream || !stream.getAudioTracks().length) {
            storeManager.dispatch(
                livestreamActions.setMicHealthCheckStatus(MIC_HEALTH_CHECK_STATUS.FAILED)
            );
            return;
        }

        // Clean up any existing monitoring
        this.stopMonitoring();

        // Set initial state to undiagnosed
        storeManager.dispatch(
            livestreamActions.setMicHealthCheckStatus(MIC_HEALTH_CHECK_STATUS.UNDIAGNOSED)
        );

        // Initialize audio analyser
        initializeAudioAnalyser(stream);

        // Start checking audio levels at regular intervals
        checkInterval = setInterval(() => {
            if (isAudioLevelAboveThreshold()) {
                // Audio level is good, mark as passed
                storeManager.dispatch(
                    livestreamActions.setMicHealthCheckStatus(MIC_HEALTH_CHECK_STATUS.PASSES)
                );

                // Stop monitoring since we've passed
                this.stopMonitoring();
            }
        }, 100); // Check every 100ms

        // Set timeout for failing the check
        timeoutId = setTimeout(() => {
            // If we reach the timeout and haven't passed, mark as failed
            storeManager.dispatch(
                livestreamActions.setMicHealthCheckStatus(MIC_HEALTH_CHECK_STATUS.FAILED)
            );

            // Stop monitoring
            this.stopMonitoring();
        }, MIC_HEALTH_CHECK_TIMEOUT_MS);
    },

    /**
     * Stop monitoring the microphone health
     */
    stopMonitoring() {
        cleanup();
    },

    /**
     * Reset the mic health check status to undiagnosed and restart monitoring
     * @param {MediaStream} stream - The audio stream to monitor
     */
    resetAndRestart(stream) {
        this.stopMonitoring();
        this.startMonitoring(stream);
    },
};

export default micHealthChecker;
